import { TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { type IFeeSplitLine, FeeSplitEditor, feeSplitSummary, useFeeSplitPayees } from 'components/FeeSplits'
import { LayoutLoading } from 'components/LayoutLoading'
import { PlainInput } from 'components/PlainInput'
import React, { useEffect, useState } from 'react'
import {
  addLoanChargeCategory,
  deleteLoanChargeCategory,
  getLoanChargeCategories,
  updateLoanChargeCategory,
} from 'services'
import { svgLoading } from 'stories/assets'
import { Modal, Toggle } from 'stories/components'
import { confirm, prompt } from 'utils'

/**
 * The fee categories a loan charge can be put in. Deliberately customer-editable rather than a
 * fixed platform list: what counts as a fee category differs per lender, and the servicing reports
 * roll charges up by whatever is configured here.
 *
 * Renaming a category rewrites it on every charge already carrying it, so history keeps matching
 * the list. Deleting only removes it from the picker; charges keep the label they were booked with.
 */
export const ServicingChargeCategories = () => {
  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState<Array<Record<string, any>>>([])
  // The category whose default split is being edited, and the working copy of that split.
  const [splitCategory, setSplitCategory] = useState<Record<string, any> | null>(null)
  const [splitRows, setSplitRows] = useState<IFeeSplitLine[]>([])
  // No loan in hand here, so party roles are offered as roles and resolve when a fee is collected.
  const payees = useFeeSplitPayees()

  useEffect(() => {
    initData()
  }, [])

  const initData = async () => {
    setLoading(true)
    const res = await getLoanChargeCategories()
    setData(res || [])
    setLoading(false)
  }

  const onAdd = async () => {
    const value = await prompt('Add New Charge Category')
    if (!value) return

    setLoading(true)
    const category = await addLoanChargeCategory({ value })
    // The server returns the existing row when the name is already taken, so refetch rather than
    // appending: that keeps a duplicate add from showing the same category twice.
    if (category) await initData()
    else setLoading(false)
  }

  const onUpdate = async (id: number, patch: Record<string, any>) => {
    setLoading(true)
    const updated = await updateLoanChargeCategory(id, patch)
    const temp = cloneDeep(data)
    const existingIdx = temp.findIndex((item) => item.id === id)
    if (existingIdx !== -1 && updated) temp[existingIdx] = updated
    setData(temp)
    setLoading(false)
  }

  const onDelete = async (item: Record<string, any>) => {
    const isContinue = await confirm(
      `Delete the "${item.value}" category? Charges already booked under it keep the label, but it will no longer be offered when adding a charge.`,
    )
    if (!isContinue) return
    setLoading(true)
    await deleteLoanChargeCategory(item.id)
    const temp = cloneDeep(data)
    const existingIdx = temp.findIndex((row) => row.id === item.id)
    if (existingIdx !== -1) temp.splice(existingIdx, 1)
    setData(temp)
    setLoading(false)
  }

  const onSaveSplit = async () => {
    if (!splitCategory) return
    const splits = splitRows
      .filter((row) => row.payeeType && Number(row.percent ?? 0) > 0)
      .map((row) => ({ ...row, percent: Number(row.percent) }))
    setSplitCategory(null)
    await onUpdate(splitCategory.id, { defaultSplits: splits })
  }

  const renderRow = (item: any, index: number) => {
    return (
      <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700" key={index}>
        <td className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap">{index + 1}</td>

        <td className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap">
          <PlainInput
            value={item.value || ''}
            content={item.value || ''}
            className={`h-[30px]`}
            onChange={(newValue: string) => newValue && onUpdate(item.id, { value: newValue })}
          />
        </td>

        <td className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap w-[100px]">
          <PlainInput
            value={String(item.sortOrder ?? 0)}
            content={String(item.sortOrder ?? 0)}
            className={`h-[30px]`}
            onChange={(newValue: string) => onUpdate(item.id, { sortOrder: Number(newValue) || 0 })}
          />
        </td>

        <td className="px-3 py-1">
          {/* Retiring a category drops it off the charge picker while leaving its history intact,
              which is what a lender wants for a fee they have stopped charging. */}
          <Toggle
            id={`charge-category-active-${item.id}`}
            value={item.isActive !== false}
            onChange={(value: boolean) => onUpdate(item.id, { isActive: value })}
          />
        </td>

        <td className="px-3 py-1 text-gray-500">
          <span
            className="cursor-pointer text-shade-blue hover:underline"
            onClick={() => {
              setSplitCategory(item)
              setSplitRows(item.defaultSplits || [])
            }}
          >
            {feeSplitSummary(item.defaultSplits, payees?.company?.name) || 'Not split'}
          </span>
        </td>

        <td className="px-3 py-1 text-gray-500">
          {item.systemKey ? 'Used by the system for charges it books automatically' : ''}
        </td>

        <td className="px-6 py-1">
          <div className="flex gap-2">
            <span className="text-red-800 p-1 hover-shadow1 cursor-pointer" onClick={() => onDelete(item)}>
              <TrashIcon className="w-4 h-4"></TrashIcon>
            </span>
          </div>
        </td>
      </tr>
    )
  }

  return (
    <div>
      <h1 className="text-2xl font-bold flex items-center pb-2">
        Charge Categories
        {isLoading && (
          <span className="ml-3">
            <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" alt="" />
          </span>
        )}
      </h1>
      <p className="text-sm text-gray-500 pb-5">
        Categories offered when adding a loan charge. Servicing reports roll charges up by these, and a
        category&apos;s fee split is what a new charge in it starts with.
      </p>

      <div className="relative overflow-auto">
        <LayoutLoading show={isLoading} />
        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
          <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-3 py-3 w-[20px]">
                No
              </th>
              <th scope="col" className="px-3 py-3">
                Name
              </th>
              <th scope="col" className="px-3 py-3">
                Order
              </th>
              <th scope="col" className="px-3 py-3">
                Active
              </th>
              <th scope="col" className="px-3 py-3">
                Fee Split
              </th>
              <th scope="col" className="px-3 py-3">
                Notes
              </th>
              <th scope="col" className="px-3 py-3">
                <button
                  className={`font-variation-settings-600 text-sm font-medium text-shade-blue hover:underline`}
                  onClick={() => onAdd()}
                >
                  + Add
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <React.Fragment key={index}>{renderRow(item, index)}</React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {splitCategory && (
        <Modal
          title={`Fee Split - ${splitCategory.value}`}
          titleOkay="Save"
          isOpen
          onClose={() => setSplitCategory(null)}
          onOk={onSaveSplit}
        >
          <div className="w-144 py-2">
            <FeeSplitEditor
              value={splitRows}
              payees={payees}
              onChange={setSplitRows}
              hint="Charges added in this category start with this split. When one is collected, each payee is queued for payout with their share; the rest stays with the loan's investors. Charges already booked keep the split they were added with."
            />
          </div>
        </Modal>
      )}
    </div>
  )
}
